<template>
    <modal dismissable show body-classes="p-0" @close="$emit('close')">
        <card class="card-form mb-0">
            <template slot="header">
                <h2 class="modal-title">Quote Calculator</h2>
            </template>
            <div v-if="showRateTableSelector" class="row mb-2">
                <div class="col-12">
                    <label class="d-block">Rate Table</label>
                    <el-select
                        class="select-primary"
                        filterable
                        :value="selectedRateTable"
                        placeholder="Select rate table"
                        @input="$emit('selectedRateTable', $event)"
                    >
                        <el-option
                            v-for="rateTable in rateTableOptions"
                            :key="rateTable"
                            class="select-primary"
                            :label="rateTable"
                            :value="rateTable"
                        />
                    </el-select>
                </div>
            </div>
            <div class="row mb-2">
                <div class="col-12">
                    <label class="d-block">Zip Code</label>
                    <el-select
                        v-model="selectedZipCodeRateId"
                        class="select-primary"
                        filterable
                        :filter-method="searchZipCode"
                        placeholder="Select or search zip code"
                    >
                        <el-option
                            v-for="rate in filteredZipCodeRates"
                            :key="rate.id"
                            class="select-primary"
                            :label="`${rate.zipcode} - ${rate.city}`"
                            :value="rate.id"
                        >
                            <div>
                                <strong>{{ rate.zipcode }}</strong> -
                                {{ rate.city }}
                            </div>
                        </el-option>
                    </el-select>
                </div>
            </div>
            <div class="row mb-2">
                <div class="col-6">
                    <label>Price Per Cabinet</label>
                </div>
                <div class="col-6">
                    <div :class="$style.subtotal">
                        {{ formatCurrency(pricePerCabinet) }}
                    </div>
                </div>
            </div>
            <div class="row mb-2">
                <div class="col-6">
                    <label>Minimum Delivery Fee</label>
                </div>
                <div class="col-6">
                    <div :class="$style.subtotal">
                        {{ formatCurrency(minimumDeliveryFee) }}
                    </div>
                </div>
            </div>
            <div class="row mb-2">
                <div class="col-12">
                    <label class="d-block"># of Cabinets</label>
                    <el-input-number v-model="numberOfCabinets" :min="0" />
                </div>
            </div>
            <div class="row mb-2">
                <div class="col-6">
                    <label>Receiving Fee</label>
                </div>
                <div class="col-6">
                    <div :class="$style.subtotal">
                        {{ formatCurrency(receivingFee) }}
                    </div>
                </div>
            </div>
            <div class="row mb-2">
                <div class="col-6">
                    <label>Delivery Fee</label>
                </div>
                <div class="col-6">
                    <div :class="$style.subtotal">
                        {{ formatCurrency(deliveryFeeSubTotal) }}
                    </div>
                </div>
            </div>
            <div class="row mb-2">
                <div class="col-6">
                    <label>Fuel Surcharge</label>
                </div>
                <div class="col-6">
                    <div :class="$style.subtotal">
                        {{ formatCurrency(fuelSurcharge) }}
                    </div>
                </div>
            </div>
            <div class="row mb-2">
                <div class="col-6">
                    <label>In House?</label>
                    <div class="form-group">
                        <el-switch
                            v-model="inHouse"
                            active-text="Yes"
                            inactive-text="No"
                        />
                    </div>
                </div>
                <div class="col-6">
                    <div :class="$style.subtotal">
                        {{ formatCurrency(inHouseDeliveryFee) }}
                    </div>
                </div>
            </div>
            <div class="row mb-2">
                <div class="col-6">
                    <label>Elevator?</label>
                    <div class="form-group">
                        <el-switch
                            v-model="hasElevator"
                            active-text="Yes"
                            inactive-text="No"
                        />
                    </div>
                </div>
                <div class="col-6">
                    <div :class="$style.subtotal">
                        {{ formatCurrency(elevatorDeliveryFee) }}
                    </div>
                </div>
            </div>
            <div class="row mb-2">
                <div class="col-6">
                    <label class="d-block"
                    >Flights of stairs (up to 14 steps/flight)</label
                    >
                    <el-input-number v-model="flightsOfStairs" :min="0" />
                </div>
                <div class="col-6">
                    <div :class="$style.subtotal">
                        {{ formatCurrency(stairDeliveryFee) }}
                    </div>
                </div>
            </div>
            <div class="row mb-2">
                <div class="col-6">
                    <label>Estimated Quote</label>
                </div>
                <div class="col-6">
                    <div :class="$style.subtotal">
                        {{ formatCurrency(estimatedTotal) }}
                    </div>
                </div>
            </div>
            <el-divider />
            <div :class="$style.actions">
                <base-button
                    type="default"
                    class="btn-fill"
                    @click="$emit('close')"
                >
                    Close
                </base-button>
            </div>
        </card>
    </modal>
</template>

<script>
import { Modal } from "src/components/UI";
import { Switch, InputNumber, Divider } from "element-ui";

export default {
    name: "QuoteCalculator",
    components: {
        Modal,
        [Switch.name]: Switch,
        [InputNumber.name]: InputNumber,
        [Divider.name]: Divider,
    },
    props: {
        selectedRateTable: {
            type: String,
            required: true,
        },
        zipCodeRates: {
            type: Array,
            required: true,
        },
        rateTableOptions: {
            type: Array,
            required: true,
        },
        showRateTableSelector: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            hasElevator: false,
            inHouse: false,
            flightsOfStairs: 0,
            numberOfCabinets: 0,
            selectedZipCodeRateId: null,
            filteredZipCodeRates: [],
            zipCodeSearch: null,
            inHouseFee: 6.0,
            cabinetReceivingFee: 8.0,
            stairFlightCarryFee: 6.0,
            elevatorFee: 6.0,
        };
    },
    computed: {
        zipCodeRate() {
            return this.zipCodeRates.find(
                (rate) => rate.id === this.selectedZipCodeRateId,
            );
        },
        pricePerCabinet() {
            if (this.zipCodeRate) {
                return this.zipCodeRate.boxRate;
            }
            return 0;
        },
        minimumDeliveryFee() {
            if (this.zipCodeRate) {
                return this.zipCodeRate.minBaseRate;
            }
            return 0;
        },
        receivingFee() {
            return this.numberOfCabinets * this.cabinetReceivingFee;
        },
        fuelSurcharge() {
            // 10% fuel surcharge
            return Math.round(this.deliveryFeeSubTotal * 0.1) || 0;
        },
        deliveryFeeSubTotal() {
            const deliveryFee = this.pricePerCabinet * this.numberOfCabinets;
            return this.minimumDeliveryFee > deliveryFee
                ? this.minimumDeliveryFee
                : deliveryFee;
        },
        deliveryFee() {
            return this.deliveryFeeSubTotal + this.fuelSurcharge;
        },
        inHouseDeliveryFee() {
            if (this.inHouse) {
                return this.numberOfCabinets * this.inHouseFee;
            }
            return 0;
        },
        elevatorDeliveryFee() {
            if (this.hasElevator) {
                return this.numberOfCabinets * this.elevatorFee;
            }
            return 0;
        },
        stairDeliveryFee() {
            return (
                this.numberOfCabinets *
                this.flightsOfStairs *
                this.stairFlightCarryFee
            );
        },
        estimatedTotal() {
            return (
                this.receivingFee +
                this.deliveryFee +
                this.inHouseDeliveryFee +
                this.elevatorDeliveryFee +
                this.stairDeliveryFee
            );
        },
    },
    watch: {
        selectedZipCodeRateId() {
            this.searchZipCode();
        },
        zipCodeRates: {
            immediate: true,
            handler(val) {
                // Try to find this zipcode in the new list of zipcode rates
                if (this.selectedZipCodeRateId) {
                    // eslint-disable-next-line max-len
                    const selectedRate = this.filteredZipCodeRates.find(
                        (rate) => rate.id === this.selectedZipCodeRateId,
                    );
                    const newSelectedRate = val.find(
                        (rate) => rate.zipcode === selectedRate.zipcode,
                    );
                    this.selectedZipCodeRateId = newSelectedRate.id;
                }

                this.filteredZipCodeRates = val;
                this.searchZipCode();
            },
        },
    },
    methods: {
        searchZipCode(search) {
            if (!search) {
                this.zipCodeSearch = null;
                this.filteredZipCodeRates = this.zipCodeRates;
                return;
            }

            this.zipCodeSearch = search.toLowerCase();
            this.filteredZipCodeRates = this.zipCodeRates.filter(
                (rate) =>
                    String(rate.zipcode).indexOf(this.zipCodeSearch) !== -1 ||
                    rate.city.toLowerCase().indexOf(this.zipCodeSearch) !== -1,
            );
        },
        formatCurrency(value) {
            const formatter = new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
            });
            return formatter.format(value);
        },
    },
};
</script>

<style module lang="scss">
.actions {
    margin-top: 30px;
    display: flex;
    justify-content: space-between;
}

.subtotal {
    text-align: right;
    font-weight: bold;
}
</style>
